import * as yup from "yup";
import { FilterDefinition } from "components/Shared/FilterEditor/utils";
import {
  createMakeGetManyFilterResult,
  getBooleanFilterDefinition,
  getSelectFilterDefinition,
  getStringFilterDefinition,
  getStringSelectFilterDefinition,
} from "components/Shared/FilterEditor/standardFilters";
import { getEnumSchema } from "utils/schemas/utils";
import EnumSelect from "components/Shared/Select/EnumSelect";
import BrandSelect from "components/Shared/Select/BrandSelect";
import {
  BrandFilter,
  BrandLayout,
  InputMaybe,
  Module,
  ModuleFilter,
} from "__generated__/gql/graphql";
import { LABELS_BY_MODULE } from "components/Layout/BrandModuleAccessGate";
import { BRAND_LAYOUT_LABELS } from "components/Admin/Brands/Manage/BrandPanel/utils";
import createNestedFilterDefinitions from "components/Shared/FilterEditor/createNestedFilterDefinitions";
import { makeEnsureType } from "utils/typescriptUtilis";
import getCommonFilterDefinitions from "utils/filterDefinitions/brand";

const filterObj = makeEnsureType<BrandFilter>();

const makeGetManyResult = createMakeGetManyFilterResult<BrandFilter>();

const baseFilterDefinitions: FilterDefinition[] = [
  ...getCommonFilterDefinitions({
    renderBrandSelect: ({ field }, { isMulti, filter }) => (
      <BrandSelect
        {...field}
        isAdmin
        isMulti={isMulti}
        menuPortalTarget={document.body}
        filter={filter}
      />
    ),
  }),
  getBooleanFilterDefinition({
    id: "isInvalidBrand",
    label: "Invalid?",
    getResult: (value, { filterVariantDefinition: { id: variantId } }) =>
      filterObj({ isInvalid: { [variantId]: value } }),
  }),
  getBooleanFilterDefinition({
    id: "isStripeCustomerAdded",
    label: "Has Stripe Customer?",
    getResult: (value, { filterVariantDefinition: { id: variantId } }) =>
      filterObj({
        stripeCustomerId: {
          isNull: variantId === "equalTo" ? !value : !!value,
        },
      }),
  }),
  getStringFilterDefinition({
    id: "stripeCustomerId",
    label: "Stripe Customer ID",
  }),
  getSelectFilterDefinition({
    id: "module",
    label: "Module",
    getSelectResult: makeGetManyResult("brandModules", (f) => ({
      module: f as unknown as InputMaybe<ModuleFilter>,
    })),
    selectSchema: getEnumSchema<Module>(Module),
    renderSelectInput: ({ field }, { isMulti }) => (
      <EnumSelect
        {...field}
        isMulti={isMulti}
        menuPortalTarget={document.body}
        enum={Module}
        labelsByValue={LABELS_BY_MODULE}
      />
    ),
  }),
  getSelectFilterDefinition({
    id: "layout",
    label: "Layout",
    getSelectResult: (value, { filterVariantDefinition: { id: variantId } }) =>
      filterObj({ layout: { [variantId]: value } }),
    selectSchema: getEnumSchema<BrandLayout>(BrandLayout),
    renderSelectInput: ({ field }, { isMulti }) => (
      <EnumSelect
        {...field}
        isMulti={isMulti}
        menuPortalTarget={document.body}
        enum={BrandLayout}
        labelsByValue={BRAND_LAYOUT_LABELS}
      />
    ),
  }),
  getBooleanFilterDefinition({
    id: "isAnalyticsSyncEnabled",
    label: "Sync analytics data to account?",
  }),
  getBooleanFilterDefinition({
    id: "isDataEntryEnabled",
    label: "Data entry access enabled?",
  }),
];

const parentFilterDefinitions = createNestedFilterDefinitions(
  "parent",
  baseFilterDefinitions.filter((fd) => !["brand", "isParent"].includes(fd.id)),
  { groupLabel: "Parent Brand" },
);

const filterDefinitions: FilterDefinition[] = [
  ...baseFilterDefinitions,
  getStringSelectFilterDefinition({
    id: "parent",
    label: "Brand",
    groupLabel: "Parent Brand",
    getResult: (value, { filterVariantDefinition: { id: variantId } }) =>
      filterObj({ parent: { name: { [variantId]: value } } }),
    getSelectResult: (value, { filterVariantDefinition: { id: variantId } }) =>
      filterObj({ parentId: { [variantId]: value } }),
    selectSchema: yup.string().uuid(),
    renderSelectInput: ({ field }, { isMulti }) => (
      <BrandSelect
        {...field}
        isAdmin
        isMulti={isMulti}
        menuPortalTarget={document.body}
        filter={{ isParent: { equalTo: true }, childBrandsExist: true }}
      />
    ),
  }),
  ...parentFilterDefinitions,
];

export default filterDefinitions;
